// Daten der Dienstleister-Detailseite (Handoff Dienstleister §9). Erst der Kern (Dienstleister, Kategorien,
// Objekte), dann die Bereiche parallel; ein fehlgeschlagener Bereich blendet nur sich selbst aus.
// Status, Volumen, Nachweise usw. rechnet dieselbe Funktion wie die Liste (baueDlZeilen).

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { heuteIso } from '../immobilien/format';
import { ladeAktivitaeten, ladeNotizen } from '../kontakte/api';
import type { Aktivitaet, KontaktNotiz } from '../kontakte/types';
import type { MieterDokument } from '../mieter/mieterDokumente';
import { baueDlZeilen, type DlObjektRoh, type DlRoh, type DlZeile, type KategorieRoh, type ObjektRoh } from './liste';
import { DL_GEAENDERT } from './useDienstleisterListe';

export type DlDetailRoh = DlRoh & Record<string, unknown>;

export interface DlRechnung {
  id: string; rechnungsnummer: string | null; bezeichnung: string | null; brutto: number | null; status: string | null; datum: string | null;
  faelligkeitsdatum: string | null; objekt_id: string | null; technisch_geprueft_am: string | null; freigegeben: boolean | null;
  bezahlt_timestamp: string | null; umlagefaehig: boolean | null; reverse_charge_13b: boolean; lohnanteil_35a: number | null;
}
export interface DlTicket {
  id: string; ticket_nummer: string | null; titel: string | null; status: string | null; prioritaet: string | null; objekt_id: string | null;
  mieter_id: string | null; created_at: string; beauftragt_am: string | null; updated_at: string | null; zustaendig_id: string | null;
  mieter: { vorname: string | null; nachname: string | null; firma_name: string | null } | null;
}
export interface DlHistorie { ticket_id: string; aktion: string; neuer_wert: string | null; created_at: string }
export interface DlNachweis { id: string; art: string; gueltig_bis: string | null; dokument_id: string | null; created_at: string }
export interface DlKontakt { id: string; name: string; rolle: string; telefon: string | null; email: string | null }
export interface DlBewertung { id: string; ticket_id: string | null; sterne: number; kriterien: Record<string, number>; kommentar: string | null; created_at: string; created_by: string | null; autor: { full_name: string | null } | null }
export interface DlTermin { id: string; titel: string | null; start_datum: string; typ: string | null; objekt_id: string | null }
export interface DlRegel { id: string; name: string | null; stichwoerter: string[]; gewerk_id: string | null; kategorie_id: string | null; prioritaet: string; ist_notfall: boolean; nur_mit_anhang: boolean; ist_rechtlich_sensibel: boolean; aktiv: boolean }

export interface DlDetailDaten {
  dienstleister: DlDetailRoh;
  kategorien: KategorieRoh[];
  kategorieIds: string[];
  zuordnungen: DlObjektRoh[];
  objekte: ObjektRoh[];
  zeile: DlZeile;
  rechnungen: DlRechnung[];
  tickets: DlTicket[];
  nachweise: DlNachweis[];
  bewertungen: DlBewertung[];
  kontakte: DlKontakt[];
  termine: DlTermin[];
  dokumente: MieterDokument[];
  notizen: KontaktNotiz[];
  aktivitaeten: Aktivitaet[];
  regeln: DlRegel[];
  /** Statuswechsel der eigenen Tickets (Reaktionszeit) */
  historie: DlHistorie[];
}

export type DlBereich = 'rechnungen' | 'tickets' | 'nachweise' | 'bewertungen' | 'kontakte' | 'termine' | 'dokumente' | 'notizen' | 'aktivitaeten' | 'regeln' | 'historie';
type Kern = Pick<DlDetailDaten, 'dienstleister' | 'kategorien' | 'kategorieIds' | 'zuordnungen' | 'objekte'>;

export type DlDetailZustand =
  | { status: 'laedt' } | { status: 'fehlt' } | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: DlDetailDaten; fehler: Partial<Record<DlBereich, string>> };

function wert<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return (r.data ?? []) as T;
}
const DOK_SPALTEN = 'id, titel, datei_name, kategorie, datei_typ, datei_url, onedrive_url, gdrive_file_id, created_at';

export async function ladeDlKern(id: string): Promise<Kern | null> {
  const d = await supabase.from('dienstleister').select('*').eq('id', id).maybeSingle();
  if (d.error) throw new Error(d.error.message);
  if (!d.data) return null;
  const [kategorien, links, zuordnungen, objekte] = await Promise.all([
    supabase.from('dienstleister_kategorien').select('id, name, ist_handwerk, aktiv, sortierung').order('sortierung').order('name'),
    supabase.from('dienstleister_kategorie').select('kategorie_id').eq('dienstleister_id', id),
    supabase.from('dienstleister_objekt').select('dienstleister_id, objekt_id, von, bis, vertragsart, aktiv, kuendigung_zum, kosten_jahr, umlagefaehig').eq('dienstleister_id', id),
    supabase.from('objekte').select('*').order('objektnummer'),
  ]);
  return {
    dienstleister: d.data as DlDetailRoh,
    kategorien: wert(kategorien) as KategorieRoh[],
    kategorieIds: (wert(links) as { kategorie_id: string }[]).map(x => x.kategorie_id),
    zuordnungen: wert(zuordnungen) as DlObjektRoh[],
    objekte: wert(objekte) as ObjektRoh[],
  };
}

async function ladeBereiche(id: string): Promise<{ daten: Pick<DlDetailDaten, DlBereich>; fehler: Partial<Record<DlBereich, string>> }> {
  const lader: { [B in DlBereich]: () => Promise<DlDetailDaten[B]> } = {
    rechnungen: async () => (wert(await supabase.from('rechnungen')
      .select('id, rechnungsnummer, bezeichnung, brutto, status, datum, faelligkeitsdatum, objekt_id, technisch_geprueft_am, freigegeben, bezahlt_timestamp, umlagefaehig, reverse_charge_13b, lohnanteil_35a')
      .eq('dienstleister_id', id).or('richtung.is.null,richtung.eq.eingang').order('datum', { ascending: false }).limit(500)) as (DlRechnung & { brutto: number | string | null; lohnanteil_35a: number | string | null })[])
      .map(r => ({ ...r, brutto: r.brutto === null ? null : Number(r.brutto), lohnanteil_35a: r.lohnanteil_35a === null ? null : Number(r.lohnanteil_35a) })),
    tickets: async () => wert(await supabase.from('tickets')
      .select('id, ticket_nummer, titel, status, prioritaet, objekt_id, mieter_id, created_at, beauftragt_am, updated_at, zustaendig_id, mieter:mieter_id(vorname, nachname, firma_name)')
      .eq('dienstleister_id', id).is('geloescht_am', null).order('created_at', { ascending: false }).limit(300)) as unknown as DlTicket[],
    historie: async () => wert(await supabase.from('ticket_historie').select('ticket_id, aktion, neuer_wert, created_at, ticket:ticket_id!inner(dienstleister_id)')
      .eq('ticket.dienstleister_id', id).eq('aktion', 'status_change').order('created_at').limit(2000)) as unknown as DlHistorie[],
    nachweise: async () => wert(await supabase.from('dienstleister_nachweise').select('id, art, gueltig_bis, dokument_id, created_at')
      .eq('dienstleister_id', id).order('gueltig_bis', { ascending: false })) as DlNachweis[],
    bewertungen: async () => wert(await supabase.from('dienstleister_bewertungen').select('id, ticket_id, sterne, kriterien, kommentar, created_at, created_by, autor:created_by(full_name)')
      .eq('dienstleister_id', id).order('created_at', { ascending: false })) as unknown as DlBewertung[],
    kontakte: async () => wert(await supabase.from('dienstleister_kontakte').select('id, name, rolle, telefon, email')
      .eq('dienstleister_id', id).order('created_at')) as DlKontakt[],
    termine: async () => wert(await supabase.from('termine').select('id, titel, start_datum, typ, objekt_id')
      .eq('dienstleister_id', id).order('start_datum', { ascending: false }).limit(100)) as DlTermin[],
    dokumente: async () => wert(await supabase.from('dokumente').select(DOK_SPALTEN)
      .eq('dienstleister_id', id).eq('is_deleted', false).order('created_at', { ascending: false }).limit(200)) as MieterDokument[],
    notizen: async () => ladeNotizen('dienstleister', id),
    aktivitaeten: async () => ladeAktivitaeten('dienstleister', id),
    regeln: async () => (wert(await supabase.from('dienstleister_regeln')
      .select('id, name, stichwoerter, gewerk_id, kategorie_id, prioritaet, ist_notfall, nur_mit_anhang, ist_rechtlich_sensibel, aktiv')
      .order('sortierung')) as DlRegel[]).map(r => ({ ...r, stichwoerter: r.stichwoerter ?? [] })),
  };
  const schluessel = Object.keys(lader) as DlBereich[];
  const ergebnisse = await Promise.allSettled(schluessel.map(s => lader[s]()));
  const daten: Record<string, unknown> = {};
  const fehler: Partial<Record<DlBereich, string>> = {};
  ergebnisse.forEach((r, i) => {
    const s = schluessel[i]!;
    if (r.status === 'fulfilled') daten[s] = r.value;
    else { daten[s] = []; fehler[s] = r.reason instanceof Error ? r.reason.message : 'Fehler'; }
  });
  return { daten: daten as Pick<DlDetailDaten, DlBereich>, fehler };
}

/** Zeile wie in der Liste, aus den Detaildaten dieses einen Dienstleisters. */
export function detailZeile(k: Kern, b: Pick<DlDetailDaten, 'rechnungen' | 'tickets' | 'nachweise' | 'bewertungen'>, heute: string): DlZeile {
  const id = k.dienstleister.id;
  return baueDlZeilen({
    dienstleister: [k.dienstleister], kategorien: k.kategorien, dlKategorien: k.kategorieIds.map(kategorie_id => ({ dienstleister_id: id, kategorie_id })),
    dlObjekte: k.zuordnungen, objekte: k.objekte,
    nachweise: b.nachweise.map(n => ({ dienstleister_id: id, art: n.art, gueltig_bis: n.gueltig_bis })),
    rechnungen: b.rechnungen.map(r => ({ dienstleister_id: id, brutto: r.brutto, status: r.status, datum: r.datum })),
    tickets: b.tickets.map(t => ({ dienstleister_id: id, status: t.status })),
    bewertungen: b.bewertungen.map(x => ({ dienstleister_id: id, sterne: x.sterne })),
  }, heute)[0]!;
}

/** Gemeldet von Panels nach Änderungen, damit Kopf, Widgets, Seitenleiste und Liste neu laden. */
export function meldeDlGeaendert(id: string): void {
  window.dispatchEvent(new CustomEvent(DL_GEAENDERT, { detail: id }));
}

export function useDienstleisterDetail(id: string): { zustand: DlDetailZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<DlDetailZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    (async () => {
      const kern = await ladeDlKern(id);
      if (!aktiv) return;
      if (!kern) { setZustand({ status: 'fehlt' }); return; }
      const { daten, fehler } = await ladeBereiche(id);
      if (!aktiv) return;
      setZustand({ status: 'ok', daten: { ...kern, ...daten, zeile: detailZeile(kern, daten, heuteIso()) }, fehler });
    })().catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [id, runde]);
  useEffect(() => {
    const hoeren = (e: Event) => { const d = (e as CustomEvent<string | null>).detail; if (!d || d === id) setRunde(x => x + 1); };
    window.addEventListener(DL_GEAENDERT, hoeren);
    return () => window.removeEventListener(DL_GEAENDERT, hoeren);
  }, [id]);
  const neuLaden = useCallback(() => setRunde(x => x + 1), []);
  return { zustand, neuLaden };
}
